import React from "react";

import { Link } from "react-router-dom";

import { motion } from "framer-motion";

import { TypeAnimation } from "react-type-animation";

import {
  ArrowRight,
  PhoneCall,
  Play,
} from "lucide-react";

import Particles from "@/components/Particles";

import heroVideo from "../assets/videos/codefix-hero.mp4";

export default function Hero() {

  return (
    <section
      className="
        relative

        min-h-screen

        overflow-hidden

        flex items-center

        bg-black
      "
      data-testid="hero-section"
    >

      {/* ===================================================== */}
      {/* VIDEO BACKGROUND */}
      {/* ===================================================== */}

      <div className="absolute inset-0 overflow-hidden">

        <video
          autoPlay
          muted
          loop
          playsInline

          className="
            absolute inset-0

            w-full h-full

            object-cover

            scale-110

            opacity-[0.55]

            brightness-[0.72]
            contrast-[1.08]
            saturate-[1.05]
          "
        >
          <source
            src={heroVideo}
            type="video/mp4"
          />
        </video>

        {/* MAIN DARK OVERLAY */}

        <div
          className="
            absolute inset-0

            bg-gradient-to-br
            from-[#05010A]/82
            via-[#12071E]/70
            to-[#06010B]/84
          "
        />

        {/* PURPLE CINEMATIC OVERLAY */}

        <div
          className="
            absolute inset-0

            bg-[radial-gradient(circle_at_top_left,rgba(168,85,247,0.22),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(192,132,252,0.18),transparent_35%)]
          "
        />

        {/* GRID */}

        <div
          className="
            absolute inset-0
            grid-bg
            opacity-[0.05]
          "
        />
      </div>

      {/* ===================================================== */}
      {/* PARTICLES */}
      {/* ===================================================== */}

      <Particles count={18} />

      {/* ===================================================== */}
      {/* GLOW EFFECTS */}
      {/* ===================================================== */}

      <div
        className="
          absolute
          top-[-180px]
          left-[-120px]

          w-[400px]
          sm:w-[700px]

          h-[400px]
          sm:h-[700px]

          bg-violet-500/20

          rounded-full

          blur-[120px]
        "
      />

      <div
        className="
          absolute
          bottom-[-180px]
          right-[-120px]

          w-[350px]
          sm:w-[650px]

          h-[350px]
          sm:h-[650px]

          bg-fuchsia-500/20

          rounded-full

          blur-[120px]
        "
      />

      {/* ===================================================== */}
      {/* CONTENT */}
      {/* ===================================================== */}

      <div
        className="
          relative z-10

          max-w-7xl
          mx-auto

          px-4 sm:px-6 md:px-8

          pt-28 sm:pt-32
          pb-20

          w-full
        "
      >

        <motion.div

          initial={{
            opacity: 0,
            y: 24,
          }}

          animate={{
            opacity: 1,
            y: 0,
          }}

          transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
          }}

          className="max-w-4xl"
        >

          {/* ===================================================== */}
          {/* BADGE */}
          {/* ===================================================== */}

          <div
            className="
              inline-flex
              items-center
              gap-2

              px-4 sm:px-5
              py-2 sm:py-2.5

              rounded-full

              bg-white/10
              backdrop-blur-xl

              border border-white/10

              text-[10px]
              sm:text-xs

              uppercase

              tracking-[0.16em]
              sm:tracking-[0.24em]

              text-violet-200

              mb-6 sm:mb-8

              shadow-lg
            "
          >

            <span
              className="
                w-2 h-2
                rounded-full
                bg-violet-400
                animate-pulse
              "
            />

            Trusted Global Technology Partner

          </div>

          {/* ===================================================== */}
          {/* HEADING */}
          {/* ===================================================== */}

          <h1
            className="
              text-4xl
              sm:text-5xl
              md:text-6xl
              lg:text-7xl

              font-bold

              leading-[1]

              tracking-tight

              text-white
            "
            style={{
              fontFamily: "Sora",
            }}
          >

            Building{" "}

            <span className="relative inline-block">

              <span
                className="
                  bg-gradient-to-r
                  from-violet-300
                  via-fuchsia-400
                  to-purple-300

                  bg-clip-text
                  text-transparent
                "
              >
                Future-Ready
              </span>

              <span
                className="
                  absolute
                  left-0
                  bottom-2

                  w-full
                  h-5 sm:h-6

                  bg-fuchsia-500/25

                  blur-2xl

                  -z-10
                "
              />

            </span>

            <br />

            Technology for{" "}

            <span
              className="
                inline-block

                min-w-[220px]
                sm:min-w-[320px]

                bg-gradient-to-r
                from-violet-300
                via-fuchsia-400
                to-purple-300

                bg-clip-text
                text-transparent
              "
            >

              <TypeAnimation
                sequence={[
                  "IT Services",
                  2000,

                  "Business Consulting",
                  2000,

                  "AI Solutions",
                  2000,

                  "Cloud Infrastructure",
                  2000,

                  "Staffing Solutions",
                  2000,
                ]}

                wrapper="span"

                speed={45}

                repeat={Infinity}

                cursor
              />

            </span>

          </h1>

          {/* ===================================================== */}
          {/* COMPANY NAME */}
          {/* ===================================================== */}

          <div
            className="
              mt-5 sm:mt-6

              text-xl
              sm:text-2xl
              md:text-3xl

              font-bold

              tracking-wide

              text-violet-300

              relative
              inline-block
            "
            style={{
              fontFamily: "Sora",
            }}
          >

            CodeFix Solutions

            <div
              className="
                absolute inset-0

                bg-violet-500/20

                blur-3xl

                -z-10
              "
            />

          </div>

          {/* ===================================================== */}
          {/* DESCRIPTION */}
          {/* ===================================================== */}

          <p
            className="
              mt-6 sm:mt-8

              max-w-2xl

              text-base
              sm:text-lg
              md:text-xl

              text-slate-200/90

              leading-relaxed
            "
          >

            We deliver premium software engineering,
            AI-powered systems, cloud services,
            staffing, and digital transformation
            strategies designed for modern enterprises
            and fast-growing startups.

          </p>

          {/* ===================================================== */}
          {/* BUTTONS */}
          {/* ===================================================== */}

          <div
            className="
              mt-10 sm:mt-12

              flex flex-col
              sm:flex-row

              sm:flex-wrap

              gap-4 sm:gap-5
            "
          >

            {/* PRIMARY */}

            <Link
              to="/contact"

              className="
                group

                w-full sm:w-auto

                inline-flex
                items-center
                justify-center

                gap-2

                px-7 sm:px-8
                py-4

                rounded-2xl

                bg-gradient-to-r
                from-violet-600
                via-fuchsia-500
                to-purple-500

                text-white
                font-semibold

                shadow-2xl
                shadow-fuchsia-500/25

                hover:scale-[1.03]
                active:scale-[0.98]

                hover:shadow-fuchsia-500/40

                transition-all duration-300
              "
            >

              Get Started

              <ArrowRight
                className="
                  w-4 h-4

                  transition-transform duration-300

                  group-hover:translate-x-1
                "
              />

            </Link>

            {/* SECONDARY */}

            <Link
              to="/services"

              className="
                w-full sm:w-auto

                inline-flex
                items-center
                justify-center

                gap-2

                px-7 sm:px-8
                py-4

                rounded-2xl

                bg-white/10
                backdrop-blur-xl

                border border-white/15

                text-white
                font-semibold

                hover:bg-white/15
                hover:border-violet-300/30

                active:scale-[0.98]

                transition-all duration-300
              "
            >

              <Play className="w-4 h-4" />

              Explore Services

            </Link>

            {/* CONSULTATION */}

            <Link
              to="/contact?type=consultation"

              className="
                w-full sm:w-auto

                inline-flex
                items-center
                justify-center

                gap-2

                px-7 sm:px-8
                py-4

                rounded-2xl

                bg-white/10
                backdrop-blur-xl

                border border-white/15

                text-white
                font-semibold

                hover:bg-white/15
                hover:border-violet-300/30

                active:scale-[0.98]

                transition-all duration-300
              "
            >

              <PhoneCall className="w-4 h-4" />

              Book Consultation

            </Link>

          </div>

          {/* ===================================================== */}
          {/* FOOTER */}
          {/* ===================================================== */}

          <motion.div

            initial={{
              opacity: 0,
            }}

            animate={{
              opacity: 1,
            }}

            transition={{
              delay: 1,
              duration: 1,
            }}

            className="
              mt-14 sm:mt-16

              text-[10px]
              sm:text-xs

              uppercase

              tracking-[0.22em]
              sm:tracking-[0.35em]

              text-slate-300/70
            "
          >

            Trusted by startups · enterprises · global teams

          </motion.div>

        </motion.div>

      </div>

    </section>
  );
}